/**
 * stageLabels.js — Central helper to translate pipeline stage keys.
 *
 * ★ 2026-08-27 (Fase 55.1) — Every CRM page has its own STAGE_LABELS
 * dictionary hardcoded in Italian. This is impossible to keep in sync
 * with i18n. This module centralises them.
 *
 * Usage:
 *   import { useStageLabel } from '@/lib/stageLabels';
 *   const stageLabel = useStageLabel();
 *   // stageLabel('lead') → 'Nuovo lead' (IT) | 'New lead' (EN) | 'Nuevo lead' (ES)
 *   stageLabel('lead', { emoji: true }) → '🆕 Nuovo lead'
 *
 * The i18n JSON is nested under `stages.<stage_key>` in each locale file.
 */
import { useTranslation } from 'react-i18next';

// Emoji map — visual identity is the same in all languages
// ★ 2026-10-05 (Fase 41) — pipeline a 14 colonne: rimossi i 5 stage di
// qualifica, aggiunto `verification` tra no_show e proposta_inviata.
export const STAGE_EMOJI = {
  lead: '🆕',
  chiamato: '📞',
  da_richiamare: '🔁',
  risposta: '💬',
  appuntamento_fissato: '📆',
  show: '🎤',
  pending_show: '⏸️',
  no_show: '⛔',
  verification: '🛡️',
  proposta_inviata: '📄',
  proposta_ricevuta: '📥',
  chiuso_cliente: '🏆',
  chiuso_partner: '🤝',
  perso: '❌',
  chiuso: '🏆',
};

// Ordered stage keys (Object.entries preserves insertion order)
export const STAGE_ORDER = Object.keys(STAGE_EMOJI);

/**
 * React hook returning a `label(stageKey, opts?)` function.
 * @returns {(key: string, opts?: { emoji?: boolean }) => string}
 */
export function useStageLabel() {
  const { t } = useTranslation();
  return (key, opts = {}) => {
    if (!key) return '—';
    const localized = t(`stages.${key}`, { defaultValue: null });
    // Fallback: use key as-is if no translation
    const label = (typeof localized === 'string' && localized.trim())
      ? localized
      : key;
    const emoji = STAGE_EMOJI[key];
    if (opts.emoji && emoji) return `${emoji} ${label}`;
    return label;
  };
}

/**
 * React hook returning `[stageKey, translatedLabel]` array (with emoji) for
 * rendering ordered filter dropdowns.
 * @returns {Array<[string, string]>}
 */
export function useOrderedStageEntries({ emoji = true } = {}) {
  const label = useStageLabel();
  return STAGE_ORDER.map((k) => [k, label(k, { emoji })]);
}

export default useStageLabel;
